//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useMemo } from 'react';
import styled from 'styled-components';

import {
  DefaultButton,
  DefaultIconButton,
} from '@components/atoms/button/Button';
import DropdownSelect, {
  IDropdownSelectItem,
} from '@components/molecules/dropdown-select/DropdownSelect';
import { TrackingIdPrefix } from '@models/TrackingId';
import { isDefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  currentPage: number;
  handlePageChange: (currentPage: number, itemsPerPage: number) => void;
  isDisabled?: boolean;
  itemsPerPage: number;
  itemsPerPageOptions?: number[];
  numItems: number;
  trackingIdPrefix?: TrackingIdPrefix;
  dropdownContentZIndex?: string;
}

interface PaginationProps {
  numPages: number;
  currentPage: number;
  isDisabled?: boolean;
  handlePageChange: (page: number) => unknown;
  trackingIdPrefix?: TrackingIdPrefix;
}

export const DEFAULT_PAGE_SIZE = 8;
export const DEFAULT_PAGE_NUMBER = 0;
export const DEFAULT_ITEMS_PER_PAGE_OPTIONS = [8, 16, 32, 64];

// Tracking Ids
const TABLE_PAGINATION_CONTROLS_TRACKING_ID = 'table-pagination-controls-';
const PREVIOUS_PAGE_BUTTON = `${TABLE_PAGINATION_CONTROLS_TRACKING_ID}previous-page-button`;
const NUMBERED_PAGE_BUTTON = `${TABLE_PAGINATION_CONTROLS_TRACKING_ID}numbered-page-button`;
const NEXT_PAGE_BUTTON = `${TABLE_PAGINATION_CONTROLS_TRACKING_ID}next-page-button`;
const RESULTS_PAGE_SIZE_DROP_DOWN = `${TABLE_PAGINATION_CONTROLS_TRACKING_ID}results-page-size-drop-down`;

export function TablePaginationControls({
  className,
  currentPage,
  dropdownContentZIndex,
  handlePageChange,
  isDisabled = false,
  itemsPerPage,
  itemsPerPageOptions = DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  numItems,
  trackingIdPrefix,
}: Props) {
  const numberPages = Math.ceil(numItems / itemsPerPage);
  const itemsPerPageSelectOptions: IDropdownSelectItem<number>[] =
    useMemo(() => {
      const options = [...itemsPerPageOptions].sort((n1, n2) => n1 - n2);
      return options.map((i) => ({
        value: i,
        label: `Show ${pluralize(i, 'Result')}`,
        trackingId: isDefined(trackingIdPrefix)
          ? `${trackingIdPrefix}-items-per-page-option`
          : undefined,
      }));
    }, [itemsPerPageOptions, trackingIdPrefix]);

  const handleCurrentPageChange = (page: number) => {
    handlePageChange(page, itemsPerPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    // trying to keep same progress (make sure first element is still visible)
    const firstVisibleElementIndex = currentPage * itemsPerPage;
    const newPage = Math.floor(firstVisibleElementIndex / newItemsPerPage);
    handlePageChange(newPage, newItemsPerPage);
  };

  return (
    <StyledControlsContainer className={className}>
      <PaginationControls
        currentPage={currentPage}
        handlePageChange={handleCurrentPageChange}
        isDisabled={isDisabled}
        numPages={numberPages}
        trackingIdPrefix={trackingIdPrefix}
      />

      {itemsPerPageOptions.length > 0 && (
        <DropdownSelect
          dropdownContentZIndex={dropdownContentZIndex}
          handleSelect={handleItemsPerPageChange}
          isDisabled={isDisabled}
          items={itemsPerPageSelectOptions}
          trackingId={
            isDefined(trackingIdPrefix)
              ? `${trackingIdPrefix}${RESULTS_PAGE_SIZE_DROP_DOWN}`
              : undefined
          }
          value={itemsPerPage}
        />
      )}
    </StyledControlsContainer>
  );
}

function PaginationControls({
  currentPage,
  handlePageChange,
  isDisabled,
  numPages,
  trackingIdPrefix,
}: PaginationProps) {
  const pages = updateVisiblePages(numPages, currentPage);

  return (
    <StyledPaginationContainer>
      <DefaultIconButton
        aria-label='previous page'
        iconName='chevron-left'
        isDisabled={numPages === 0 || currentPage === 0 || isDisabled}
        onClick={() => handlePageChange(currentPage - 1)}
        trackingId={
          isDefined(trackingIdPrefix)
            ? `${trackingIdPrefix}${PREVIOUS_PAGE_BUTTON}`
            : undefined
        }
      />

      {pages.map(({ label, page }, index) => (
        <PaginationButton
          key={`${page}-${index}`}
          isActive={page === currentPage && !isDisabled}
          isDisabled={isDisabled}
          onClick={() => handlePageChange(page)}
          trackingId={
            isDefined(trackingIdPrefix)
              ? `${trackingIdPrefix}${NUMBERED_PAGE_BUTTON}`
              : undefined
          }
        >
          {label}
        </PaginationButton>
      ))}

      <DefaultIconButton
        aria-label='next page'
        iconName='chevron-right'
        isDisabled={
          numPages === 0 || currentPage === numPages - 1 || isDisabled
        }
        onClick={() => handlePageChange(currentPage + 1)}
        trackingId={
          isDefined(trackingIdPrefix)
            ? `${trackingIdPrefix}${NEXT_PAGE_BUTTON}`
            : undefined
        }
      />
    </StyledPaginationContainer>
  );
}

const updateVisiblePages = (
  numPages: number,
  currentPage: number,
  numPageControls = 3,
) => {
  if (numPages === 0) {
    return [];
  }

  const firstNum = currentPage - (currentPage % numPageControls);
  const visiblePages = Array.from(
    { length: Math.min(numPageControls, numPages - firstNum) },
    (_, i) => {
      const page = firstNum + i;
      return { page, label: `${page + 1}` };
    },
  );

  const firstItem = visiblePages[0]?.page;
  if (isDefined(firstItem) && firstItem > 1) {
    visiblePages.unshift({ page: firstItem - 1, label: '...' });
    visiblePages.unshift({ page: 0, label: '1' });
  }

  const lastItem = visiblePages[visiblePages.length - 1]?.page;
  if (isDefined(lastItem) && lastItem < numPages - 1) {
    if (lastItem < numPages - 2) {
      visiblePages.push({ page: lastItem + 1, label: '...' });
    }
    visiblePages.push({ page: numPages - 1, label: `${numPages}` });
  }

  return visiblePages;
};

const PaginationButton = styled(DefaultButton)<{ isActive?: boolean }>(
  ({ isActive, theme }) => `
  && button { padding: ${theme.spacings[2]} }
  ${
    isActive
      ? `
    background-color: ${theme.colors.white};
    box-shadow: inset 0 0 0 1px ${theme.colors.gray9};
    `
      : ''
  }
`,
);

const StyledControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledPaginationContainer = styled.div(
  ({ theme }) => `
  display: flex;
  gap: ${theme.spacings[1]}
  `,
);
